import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import {
  Dropdown,
  OperatorDropdown,
  OperatorContainer,
  Operator,
  Label,
  ConditionRow,
  HeaderContent,
  HeaderIcon,
  DropdownHeader,
  SearchInputContainer,
  DropdownSearchInput,
} from './styles';
import { operatorOptions, demographicsAttributes, questionTypeMatrix } from '../../constants';
import { mapCountries } from '../../../../../../constants/countries';

const Condition = ({
  condition,
  index,
  conditionsLength,
  handleConditionChange,
  addCondition,
  removeCondition,
  filterOptions,
  attributeOptions,
  allQuestions,
}) => {
  const [collapsedSections, setCollapsedSections] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [attributeDropdownOpen, setAttributeDropdownOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [staticValueOptions, setStaticValueOptions] = useState([]);
  const hasSelectedValueRef = useRef(false);

  useEffect(() => {
    const hasValue = (Array.isArray(condition.value) && condition.value.length > 0)
      || (typeof condition.value === 'string' && condition.value !== '');
    hasSelectedValueRef.current = hasValue;
  }, [condition.value]);

  const getOptionText = (attribute, key, count) => {
    const responseText = `${count} responses`;
    const countryText = `${mapCountries[key]} (${responseText})`;
    const defaultText = `${key} (${responseText})`;

    if (attribute === 'country') return countryText;
    return defaultText;
  };

  const createValueOptions = () => {
    if (!condition.attribute) return [];
    let valueOptions = [];

    if (demographicsAttributes.includes(condition.attribute)) {
      const attributeKey = condition.attribute;
      valueOptions = Object.entries(filterOptions[attributeKey] || {}).map(([key, count]) => ({
        key,
        text: getOptionText(condition.attribute, key, count),
        value: key,
        count,
      }));
    } else {
      const question = allQuestions.find((q) => q.id === condition.attribute);

      if (question && question.questionType !== questionTypeMatrix && question.questionOptions) {
        valueOptions = question.questionOptions.map((option, idx) => ({
          key: option.id ? option.id : `${option.label}_uniqueIndex_${idx}`,
          text: option.label,
          value: option.id,
        }));
      } else if (question && question.questionType === questionTypeMatrix) {
        const rowOptions = question.questionOptions.filter((opt) => opt.matrixOption === 'row');
        const colOptions = question.questionOptions.filter((opt) => opt.matrixOption === 'column');

        const combinedMatrixOptions = rowOptions.flatMap((row) => colOptions.map((col) => ({
          key: `${row.id}-${col.id}`,
          text: `${row.label} - ${col.label}`,
          value: { [row.id]: col.id },
        })));

        valueOptions = combinedMatrixOptions;
      }
    }

    return valueOptions;
  };

  const handleOperatorChange = (value) => {
    handleConditionChange(condition.id, 'operator', value);
  };

  const handleValueChange = (newValue) => {
    handleConditionChange(condition.id, 'value', newValue);
  };

  const toggleGroup = (key) => {
    setCollapsedSections((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };

  const renderMenuItems = () => {
    if (searchTerm) {
      // Flatten and filter all options
      const allOptions = attributeOptions.reduce(
        (acc, group) => [...acc, ...group.options],
        [],
      );
      const filteredOptions = allOptions.filter((option) => option.text.toLowerCase().includes(searchTerm.toLowerCase()));
      return filteredOptions.map((option) => (
        <Dropdown.Item
          key={option.key}
          text={option.text}
          value={option.value}
          onClick={(e, data) => {
            handleConditionChange(condition.id, 'attribute', data.value);
            setAttributeDropdownOpen(false);
          }}
        />
      ));
    }
    // Render grouped options
    return attributeOptions.map((group) => (
      <React.Fragment key={group.key}>
        <DropdownHeader
          onClick={(e) => {
            e.stopPropagation();
            toggleGroup(group.key);
          }}
        >
          <HeaderContent>
            <HeaderIcon name={collapsedSections[group.key] ? 'caret right' : 'caret down'} />
            {group.text}
          </HeaderContent>
        </DropdownHeader>
        {!collapsedSections[group.key]
          && group.options.map((option) => (
            <Dropdown.Item
              key={option.key}
              text={option.text}
              value={option.value}
              onClick={(e, data) => {
                handleConditionChange(condition.id, 'attribute', data.value);
                setAttributeDropdownOpen(false);
              }}
            />
          ))}
      </React.Fragment>
    ));
  };

  const valueOptions = useMemo(() => createValueOptions(), [condition]);
  const displayedOptions = dropdownOpen ? staticValueOptions : valueOptions;

  return (
    <ConditionRow>
      <Label>{index === 0 ? 'Filter' : 'AND'}</Label>
      <Dropdown
        selection
        value={condition.attribute}
        open={attributeDropdownOpen}
        onOpen={() => setAttributeDropdownOpen(true)}
        onClose={() => setAttributeDropdownOpen(false)}
        className="attribute-dropdown"
        text={
          attributeOptions
            .find((group) => group.options.some((option) => option.value === condition.attribute))
            ?.options.find((option) => option.value === condition.attribute)?.text || ''
        }
      >
        <Dropdown.Menu>
          <SearchInputContainer
            role="button"
            tabIndex={0}
            aria-label="Search input container"
          >
            <DropdownSearchInput
              icon="search"
              iconPosition="right"
              placeholder="Search attribute"
              onChange={(e) => {
                setSearchTerm(e.target.value);
                e.stopPropagation();
              }}
              value={searchTerm}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          </SearchInputContainer>
          {renderMenuItems(condition)}
        </Dropdown.Menu>
      </Dropdown>
      <OperatorDropdown
        selection
        closeOnChange
        options={operatorOptions}
        value={condition.operator}
        onChange={(e, { value }) => handleOperatorChange(value)}
      />
      <Dropdown
        multiple
        selection
        options={displayedOptions}
        value={condition.value}
        onChange={(e, { value }) => {
          handleValueChange(value);
        }}
        onOpen={() => {
          setDropdownOpen(true);
          setStaticValueOptions(valueOptions);
        }}
        onClose={() => {
          setDropdownOpen(false);
        }}
      />
      <OperatorContainer>
        <Operator onClick={addCondition}>+</Operator>
        {conditionsLength > 1 && (
          <Operator onClick={() => removeCondition(condition.id)}>-</Operator>
        )}
      </OperatorContainer>
    </ConditionRow>
  );
};

Condition.propTypes = {
  condition: PropTypes.shape({
    id: PropTypes.string.isRequired,
    attribute: PropTypes.string,
    operator: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  }).isRequired,
  index: PropTypes.number.isRequired,
  conditionsLength: PropTypes.number.isRequired,
  handleConditionChange: PropTypes.func.isRequired,
  addCondition: PropTypes.func.isRequired,
  removeCondition: PropTypes.func.isRequired,
  filterOptions: PropTypes.object.isRequired,
  attributeOptions: PropTypes.array.isRequired,
  allQuestions: PropTypes.array.isRequired,
};

export default Condition;
